import { Rng } from "./rng.js";
import type { Cell, GameState, Goal, GoalProgress, LevelDef, Move, MoveResult, Pos } from "./types.js";
import {
  applyGravity,
  cellAt,
  findGroup,
  hasAnyMove,
  inBounds,
  randomColorCell,
  refill,
  shuffleColors,
} from "./board.js";

/** Group size thresholds for creating specials. Tunable via experiments (config), not code. */
export interface Rules {
  rocketAt: number; // group size >= rocketAt creates a rocket
  bombAt: number; // group size >= bombAt creates a bomb
}

export const DEFAULT_RULES: Rules = { rocketAt: 5, bombAt: 8 };

export function parseLayout(level: LevelDef, rng: Rng): Cell[][] {
  const grid: Cell[][] = [];
  for (let r = 0; r < level.height; r++) {
    const row: Cell[] = [];
    const line = level.layout?.[r];
    for (let c = 0; c < level.width; c++) {
      const ch = line?.[c] ?? ".";
      if (ch === "X") row.push({ kind: "crate", hp: 1 });
      else if (ch === "Y") row.push({ kind: "crate", hp: 2 });
      else if (ch >= "0" && ch <= "9") row.push({ kind: "color", color: Number(ch) % level.colors });
      else row.push(randomColorCell(rng, level.colors));
    }
    grid.push(row);
  }
  return grid;
}

export function createGame(level: LevelDef): { state: GameState; rng: Rng } {
  const rng = new Rng(level.seed);
  const grid = parseLayout(level, rng);
  // Guarantee a playable start.
  let guard = 0;
  while (!hasAnyMove(grid) && guard++ < 20) shuffleColors(grid, rng);
  const state: GameState = {
    level,
    grid,
    movesLeft: level.moves,
    progress: level.goals.map((goal) => ({ goal, done: 0 })),
    status: "playing",
    history: [],
    clearedTotal: 0,
    bestGroup: 0,
  };
  return { state, rng };
}

function creditGoal(progress: GoalProgress[], cell: Cell): void {
  if (!cell) return;
  for (const p of progress) {
    const g: Goal = p.goal;
    if (g.type === "color" && cell.kind === "color" && cell.color === g.color) p.done++;
    if (g.type === "crate" && cell.kind === "crate") p.done++;
    if (g.type === "clear") p.done++;
  }
}

export function goalsMet(progress: GoalProgress[]): boolean {
  return progress.every((p) => p.done >= p.goal.count);
}

/** 0..1 overall goal completion, drives the pixel reveal. */
export function revealFraction(progress: GoalProgress[]): number {
  const total = progress.reduce((a, p) => a + p.goal.count, 0);
  if (total === 0) return 1;
  const done = progress.reduce((a, p) => a + Math.min(p.done, p.goal.count), 0);
  return done / total;
}

/**
 * Resolve a set of cells to clear, expanding through specials (chain reactions) and damaging
 * adjacent crates. Mutates grid (sets cleared cells to null). Returns positions cleared and
 * specials activated.
 */
function resolveClear(
  grid: Cell[][],
  initial: Pos[],
  progress: GoalProgress[],
): { cleared: Pos[]; activated: Pos[] } {
  const h = grid.length;
  const w = grid[0]?.length ?? 0;
  const mark = new Uint8Array(h * w); // 1 = scheduled to clear
  const cleared: Pos[] = [];
  const activated: Pos[] = [];
  const queue: Pos[] = [...initial];

  const schedule = (r: number, c: number) => {
    if (!inBounds(grid, r, c)) return;
    const idx = r * w + c;
    if (mark[idx]) return;
    const cell = grid[r]![c];
    if (!cell) return;
    if (cell.kind === "crate") {
      // crates take damage from blasts, never "cleared" directly by a rocket sweep more than 1/turn
      return;
    }
    mark[idx] = 1;
    queue.push({ r, c });
  };

  const damagedCrates = new Set<number>();
  const damageCrateAround = (r: number, c: number) => {
    for (const [dr, dc] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
    ] as const) {
      const nr = r + dr;
      const nc = c + dc;
      if (!inBounds(grid, nr, nc)) continue;
      const cell = grid[nr]![nc];
      if (cell && cell.kind === "crate") damagedCrates.add(nr * w + nc);
    }
  };

  for (const p of initial) mark[p.r * w + p.c] = 1;

  while (queue.length) {
    const p = queue.shift()!;
    const cell = grid[p.r]![p.c];
    if (!cell) continue;
    if (cell.kind === "rocket") {
      activated.push(p);
      if (cell.dir === "h") for (let c = 0; c < w; c++) schedule(p.r, c);
      else for (let r = 0; r < h; r++) schedule(r, p.c);
      // rockets also crack crates in their path
      if (cell.dir === "h") for (let c = 0; c < w; c++) damageCrateAround(p.r, c);
      else for (let r = 0; r < h; r++) damageCrateAround(r, p.c);
    } else if (cell.kind === "bomb") {
      activated.push(p);
      for (let dr = -1; dr <= 1; dr++)
        for (let dc = -1; dc <= 1; dc++) {
          schedule(p.r + dr, p.c + dc);
          damageCrateAround(p.r + dr, p.c + dc);
        }
    } else if (cell.kind === "color") {
      damageCrateAround(p.r, p.c);
    }
    creditGoal(progress, cell);
    grid[p.r]![p.c] = null;
    cleared.push(p);
  }

  for (const idx of damagedCrates) {
    const r = Math.floor(idx / w);
    const c = idx % w;
    const cell = grid[r]![c];
    if (cell && cell.kind === "crate") {
      cell.hp -= 1;
      if (cell.hp <= 0) {
        creditGoal(progress, cell);
        grid[r]![c] = null;
        cleared.push({ r, c });
      }
    }
  }

  return { cleared, activated };
}

/**
 * Apply a tap. Pure with respect to (state, rng): same inputs => same outputs.
 * Mutates state and rng (call on a clone to preview).
 */
export function applyMove(
  state: GameState,
  rng: Rng,
  move: Move,
  rules: Rules = DEFAULT_RULES,
): MoveResult {
  const empty: MoveResult = {
    valid: false,
    cleared: [],
    created: [],
    activated: [],
    falls: [],
    spawned: [],
    shuffled: false,
    groupSize: 0,
    status: state.status,
  };
  if (state.status !== "playing") return empty;
  const { grid } = state;
  const target = cellAt(grid, move.r, move.c);
  if (!target || target.kind === "crate") return empty;

  let initial: Pos[] = [];
  let groupSize = 0;
  const created: { pos: Pos; cell: Cell }[] = [];

  if (target.kind === "color") {
    const group = findGroup(grid, move.r, move.c);
    if (group.length < 2) return empty;
    groupSize = group.length;
    initial = group;
  } else {
    initial = [{ r: move.r, c: move.c }];
  }

  const { cleared, activated } = resolveClear(grid, initial, state.progress);

  // Create a special at the tapped cell for big groups.
  if (target.kind === "color") {
    let special: Cell = null;
    if (groupSize >= rules.bombAt) special = { kind: "bomb" };
    else if (groupSize >= rules.rocketAt) special = { kind: "rocket", dir: rng.int(2) === 0 ? "h" : "v" };
    if (special) {
      grid[move.r]![move.c] = special;
      created.push({ pos: { r: move.r, c: move.c }, cell: special });
    }
  }

  const falls = applyGravity(grid);
  const spawned = refill(grid, rng, state.level.colors);

  let shuffled = false;
  let guard = 0;
  while (!hasAnyMove(grid) && guard++ < 20) {
    shuffleColors(grid, rng);
    shuffled = true;
  }

  state.movesLeft -= 1;
  state.history.push(move);
  state.clearedTotal += cleared.length;
  if (groupSize > state.bestGroup) state.bestGroup = groupSize;

  if (goalsMet(state.progress)) state.status = "won";
  else if (state.movesLeft <= 0) state.status = "lost";

  return {
    valid: true,
    cleared,
    created,
    activated,
    falls,
    spawned,
    shuffled,
    groupSize,
    status: state.status,
  };
}

/** Replay a move list from scratch; used by bot evaluation, video factory and anti-cheat. */
export function replay(level: LevelDef, moves: Move[], rules: Rules = DEFAULT_RULES): GameState {
  const { state, rng } = createGame(level);
  for (const m of moves) {
    if (state.status !== "playing") break;
    applyMove(state, rng, m, rules);
  }
  return state;
}
